import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { ShieldCheck, MapPin, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Iter-77 (part 5) — Supervisor sees a top-of-page "Regional Assignment"
// card when the Regional Manager (or Admin) has delegated a scope to them.
// It surfaces exactly what the manager pushed down: assigned cities,
// assigned communities, and the manager who delegated it. From here the
// supervisor continues into HierarchyAssignments (below) to further
// delegate to crew leaders and site agents.
//
// Iter-78 (part 7) — extended to also render for role=manager / company_
// manager / admin. In that case we synthesize a "full region scope" card
// showing every city & community in the RM's region (they own the entire
// region, so there's no upstream delegation — the card serves as the
// definitive listing of what they can hand off).
export const SupervisorRegionalAssignmentCard = ({ user }) => {
  const [row, setRow] = useState(null);
  const [propNames, setPropNames] = useState({});

  const isManagerView = ['manager', 'company_manager', 'admin'].includes(user?.role);

  useEffect(() => {
    if (!user?.user_id) return;
    (async () => {
      try {
        // Fetch property names + regional scope regardless of role — the
        // manager view needs the full region's community list.
        let regionEntry = null;
        try {
          const tv = await fetch(`${BACKEND_URL}/api/routes/team-view`, { credentials: 'include' });
          if (tv.ok) {
            const tvData = await tv.json();
            const props = (tvData || []).flatMap((e) => e.properties || []);
            const map = {};
            props.forEach((p) => { map[p.property_id] = p.property_name; });
            setPropNames(map);
            // Locate the entry for the RM's own region.
            if (isManagerView) {
              const entries = (tvData || []).filter(
                (e) => e.region?.region_id && e.region.region_id !== '_unassigned',
              );
              regionEntry = user?.region_id
                ? entries.find((e) => e.region.region_id === user.region_id) || entries[0]
                : entries[0];
            }
          }
        } catch (_) { /* silent */ }

        if (isManagerView && regionEntry) {
          // Synthesize an "assignment row" that spans the whole region so
          // the same UI works for the RM.
          const cities = Array.from(new Set(
            (regionEntry.properties || []).map((p) => p.city).filter(Boolean),
          )).sort();
          setRow({
            assigned_by_name: 'the Executive Office',
            region_code: regionEntry.region?.region_code,
            assigned_cities: cities,
            assigned_properties: (regionEntry.properties || []).map((p) => p.property_id),
            _mode: 'manager',
          });
          return;
        }

        // Supervisor path — read the delegated assignment for this user.
        const res = await fetch(`${BACKEND_URL}/api/company/staff-assignments`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const mine = (data || []).find((a) => a.user_id === user.user_id);
        if (!mine) return;
        setRow(mine);
      } catch (_) { /* silent */ }
    })();
  }, [user?.user_id, user?.region_id, isManagerView]);

  if (!row || (!row.assigned_cities?.length && !row.assigned_properties?.length)) return null;

  const isManagerCard = row._mode === 'manager';

  return (
    <Card
      className="bg-gradient-to-br from-[#C9A962]/15 to-zinc-900 border-2 border-[#C9A962]/60 p-5 mb-4"
      data-testid={isManagerCard ? 'manager-regional-assignment-card' : 'supervisor-regional-assignment-card'}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <p
            className="text-[11px] uppercase tracking-widest text-[#C9A962] font-bold mb-1"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.14em' }}
          >
            <ShieldCheck className="h-3.5 w-3.5 inline mr-1" />
            {isManagerCard ? 'My Region Scope' : 'My Regional Assignment'}
          </p>
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {isManagerCard
              ? `Full control · ${row.region_code || ''} ${row.assigned_cities.length} cities · ${row.assigned_properties.length} communities`
              : `Delegated by ${row.assigned_by_name || 'Regional Manager'}`}
          </h3>
        </div>
        {row.region_code && (
          <Badge className="bg-[#C9A962] text-black font-bold text-xs" data-testid="supervisor-assignment-region">
            {row.region_code}
          </Badge>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase text-zinc-400 mb-1.5 font-bold flex items-center gap-1">
            <MapPin className="h-3 w-3" /> Cities in your scope
          </p>
          <div className="flex flex-wrap gap-1" data-testid="supervisor-assignment-cities">
            {(row.assigned_cities || []).length === 0
              ? <span className="text-xs text-zinc-500">All cities in region</span>
              : row.assigned_cities.map((c) => (
                <Badge key={c} className="bg-blue-500/15 border border-blue-500/40 text-blue-200 text-[10px]">
                  {c}
                </Badge>
              ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase text-zinc-400 mb-1.5 font-bold flex items-center gap-1">
            <Building2 className="h-3 w-3" /> Communities in your scope
          </p>
          <div className="flex flex-wrap gap-1" data-testid="supervisor-assignment-properties">
            {(row.assigned_properties || []).length === 0
              ? <span className="text-xs text-zinc-500">No specific communities pinned</span>
              : row.assigned_properties.slice(0, 12).map((pid) => (
                <Badge key={pid} className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-[10px]">
                  {propNames[pid] || pid.slice(-6)}
                </Badge>
              ))}
            {(row.assigned_properties || []).length > 12 && (
              <Badge className="bg-zinc-700 text-zinc-200 text-[10px]">
                +{row.assigned_properties.length - 12} more
              </Badge>
            )}
          </div>
        </div>
      </div>

      <p className="mt-3 text-[10px] text-zinc-500 leading-snug">
        {isManagerCard
          ? 'Delegate cities & communities to each supervisor in the Hierarchy Assignments panel below — every save auto-populates that supervisor\u2019s dashboard.'
          : 'Assign crew leaders & site agents to these communities from the Hierarchy Assignments panel below.'}
      </p>
    </Card>
  );
};
